// src/native/ui/nativeSplash.js
//
// Cold start without a white flash.
//
// capacitor.config.ts sets `SplashScreen.launchAutoHide: false` and a
// background of #1a1d2e that matches the theme-color in index.html. That means
// the native splash stays up until JavaScript takes it down, so the user never
// sees the webview's default white page while the bundle boots. This module is
// what takes it down.
//
// The failsafe matters more than the happy path: an app stuck on a splash
// screen is an App Store rejection under 2.1 and looks like a hang to the user.
// So the splash is hidden on a timer no matter what React does, and hiding is
// idempotent.

import { isNative, loadNative } from '@/lib/platform';

/** Hard ceiling. If the first route has not painted by now, show it anyway. */
const SPLASH_FAILSAFE_MS = 3500;

let hidePromise = null;
let failsafeTimer = null;

/**
 * Hide the native splash screen. Safe to call any number of times and from any
 * number of places: the first call wins and later calls await the same promise.
 * Resolves immediately and does nothing on the web.
 *
 * @returns {Promise<void>}
 */
export function hideNativeSplash() {
  if (!isNative()) return Promise.resolve();
  if (hidePromise) return hidePromise;

  hidePromise = (async () => {
    if (failsafeTimer) {
      clearTimeout(failsafeTimer);
      failsafeTimer = null;
    }
    const mod = await loadNative(() => import('@capacitor/splash-screen'));
    try {
      await mod?.SplashScreen?.hide({ fadeOutDuration: 200 });
    } catch {
      /* Plugin missing or already hidden. Never block boot on this. */
    }
  })();

  return hidePromise;
}

/**
 * Arm the "hide it regardless" timer. Call this as early as the app tree
 * mounts. No-op on the web and no-op if the splash is already going down.
 */
export function armNativeSplashFailsafe() {
  if (!isNative() || hidePromise || failsafeTimer) return;
  failsafeTimer = setTimeout(() => {
    failsafeTimer = null;
    void hideNativeSplash();
  }, SPLASH_FAILSAFE_MS);
}

/**
 * Hide the splash once the first frame containing real UI has actually been
 * painted. Two nested animation frames put us after layout and paint of the
 * current commit, which is the point where swapping the splash out is
 * invisible. Falls back to a plain timeout when rAF is unavailable.
 */
export function scheduleNativeSplashHide() {
  if (!isNative()) return;

  const fire = () => {
    void hideNativeSplash();
  };

  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    setTimeout(fire, 0);
    return;
  }
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(fire);
  });
}
